import { useEffect, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight } from "lucide-react";
import {
  ApiError,
  getAuthMode,
  login,
  requestPasswordReset,
  setAuthToken,
} from "@/lib/api";
import { AuthProductShell } from "@/features/auth/components/AuthProductShell";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { useMe } from "@/features/auth/useMe";
import { unscopedKey } from "@/lib/queryClient";
import {
  authSearchParam,
  isLegacyRequested,
  sanitizeNextPath,
} from "@/features/auth/authPaths";
import { KratosLoginPanel } from "@/features/auth/kratos/KratosLoginPanel";

/**
 * M9a A1–A7 — `/login` real-UI.
 *
 * Replaces the M4 `<InlineLoginPanel>` placeholder. Three concerns:
 *   1. Username + Password form → POST /api/login → setAuthToken
 *      → invalidate all queries → navigate to `?next=` (or `/hub`,
 *      which redirects to the user's role-based home).
 *   2. "Forgot password?" link → `<PasswordResetRequestModal>` →
 *      POST /api/auth/password-reset/request. Generic
 *      enumeration-safe response. Dev-token Copy-link when the
 *      backend returns `reset_token` (TKTIC_DEV_TOKEN_DELIVERY=1).
 *   3. Session-resume on mount — if `/api/me` already returns a
 *      user (cached token still valid), skip the form and go to
 *      `next` immediately.
 *
 * The page does NOT mount the AdminShell or any other authed
 * shell — it's a standalone route at /login.
 */
export function LoginPage() {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ from: "/login" });
  const nextPath = sanitizeNextPath(search.next ?? authSearchParam("next"));
  const legacyRequested =
    isLegacyRequested(search.legacy) ||
    isLegacyRequested(authSearchParam("legacy"));
  const authErrorCode = search.auth_error ?? authSearchParam("auth_error");
  const authError = authErrorCopy(authErrorCode);
  const isVerificationRequired = authErrorCode === "verified_email_required";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const authMode = useQuery({
    queryKey: unscopedKey("auth-mode"),
    queryFn: getAuthMode,
    staleTime: 60_000,
  });
  const kratosEnabled = authMode.data?.kratos_enabled ?? false;
  const legacyEnabled = authMode.data
    ? authMode.data.legacy_enabled
    : authMode.isError;
  // Dual mode keeps the legacy password form reachable via ?legacy=1;
  // kratos-only installs render the Kratos panel exclusively.
  const showKratosPanel = kratosEnabled && !legacyRequested && !me.data?.user;
  const showPasswordForm =
    legacyEnabled && (!kratosEnabled || legacyRequested);
  const showForgotPassword = legacyEnabled && !isVerificationRequired;
  const showSelfServiceSignup = !legacyRequested && !isVerificationRequired;
  // The Kratos panel renders its own recovery/register footer.
  const showAuthFooter =
    !authError &&
    !kratosEnabled &&
    (showForgotPassword ||
      showSelfServiceSignup ||
      (nextPath && nextPath !== "/" && nextPath !== "/hub"));

  // A5 — session-resume. If /api/me already returns a user, the
  // operator is already signed in (probably hit /login by mistake
  // or via a bookmark). Send them where they were trying to go.
  useEffect(() => {
    if (me.data?.user && !authError) {
      void navigate({ to: nextPath, replace: true });
    }
  }, [authError, me.data, nextPath, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await login({ username, password });
      setAuthToken(resp.token);
      // Invalidate everything so sibling caches refetch with the
      // new auth context. Includes the unscoped `me` query so the
      // session-resume effect picks up the fresh user.
      await queryClient.invalidateQueries();
      // Belt + suspenders: explicitly invalidate `me` since some
      // surfaces gate on `useMe().data` directly.
      await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      void navigate({ to: nextPath, replace: true });
    } catch (err) {
      setError(formatLoginErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthProductShell
      testId="login-page"
      title={
        authError
          ? authErrorPageTitle(authErrorCode)
          : isVerificationRequired
            ? "Please verify your email"
            : "Sign in"
      }
      description={
        authError
          ? authErrorPageDescription(authErrorCode)
          : isVerificationRequired
          ? "Check your inbox and verify your email before continuing to your workspace."
          : kratosEnabled && !legacyRequested
          ? "Sign in with your email and password, or use a passkey."
          : "Enter your username and password to open the workspace assigned to your account."
      }
    >
      <div className="flex flex-col gap-4">
        {authMode.isPending ? (
          <p
            data-testid="login-auth-mode-loading"
            className="font-mono text-xs text-ink-muted"
          >
            Checking sign-in options...
          </p>
        ) : null}

        {authError ? (
          <section
            data-testid="login-auth-error"
            className="rounded border border-destructive/50 bg-destructive/10 p-4"
          >
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-destructive/20 text-destructive">
                <AlertTriangle aria-hidden="true" className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <h2 className="font-display text-base font-semibold text-ink">
                  {authErrorTitle(authErrorCode)}
                </h2>
                <p className="mt-1 text-xs leading-5 text-ink-muted">
                  {authError}
                </p>
              </div>
            </div>
          </section>
        ) : null}

        {showKratosPanel ? (
          <KratosLoginPanel
            nextPath={nextPath}
            flowId={typeof search.flow === "string" ? search.flow : undefined}
          />
        ) : null}

        {showPasswordForm ? (
          <form
            onSubmit={handleSubmit}
            data-testid="login-form"
            className="flex flex-col gap-4"
          >
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-display uppercase tracking-wider text-ink-muted">
                Username
              </span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                required
                data-testid="login-username"
                className="rounded border border-rule bg-canvas px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-display uppercase tracking-wider text-ink-muted">
                Password
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                data-testid="login-password"
                className="rounded border border-rule bg-canvas px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </label>

            {error ? (
              <p
                data-testid="login-error"
                className="text-destructive text-xs font-mono"
              >
                {error}
              </p>
            ) : null}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="w-full sm:w-auto sm:self-end"
            >
              {submitting ? (
                "Signing in..."
              ) : (
                <>
                  Sign in
                  <ArrowRight aria-hidden="true" />
                </>
              )}
            </Button>
          </form>
        ) : null}

        {showAuthFooter ? (
          <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-rule pt-3 text-[0.65rem] font-mono text-ink-muted">
            {showForgotPassword ? (
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                data-testid="login-forgot"
                className="rounded px-1 py-0.5 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                Forgot password?
              </button>
            ) : null}
            {showSelfServiceSignup ? (
              <Link
                to="/register"
                data-testid="login-register"
                className="rounded px-1 py-0.5 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                Create account
              </Link>
            ) : null}
            {nextPath && nextPath !== "/" && nextPath !== "/hub" ? (
              <span data-testid="login-next-target">→ {nextPath}</span>
            ) : null}
          </footer>
        ) : null}
      </div>

      {forgotOpen && legacyEnabled ? (
        <PasswordResetRequestModal
          initialUsername={username}
          onClose={() => setForgotOpen(false)}
        />
      ) : null}
    </AuthProductShell>
  );
}

// ---------------------------------------------------------------------------
// A7 — Forgot password request modal
// ---------------------------------------------------------------------------

function PasswordResetRequestModal({
  initialUsername,
  onClose,
}: {
  initialUsername: string;
  onClose: () => void;
}) {
  const [username, setUsername] = useState(initialUsername);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  // Privacy invariant: the dev-token surfaces ONLY when the
  // backend response carries it (TKTIC_DEV_TOKEN_DELIVERY=1).
  // Never logged; surfaces via a gated Copy-link control.
  const [devResetToken, setDevResetToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (submitting) return;
    if (!username.trim()) {
      setError("Username is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const resp = await requestPasswordReset({ username: username.trim() });
      // The response is enumeration-safe — same shape regardless
      // of whether the username exists. Only the dev-token branch
      // distinguishes.
      setSuccess(true);
      if (resp.reset_token) {
        setDevResetToken(resp.reset_token);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Could not send reset request.",
        );
      } else {
        setError("Network error. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      title="Reset password"
      message={
        success ? (
          <div className="flex flex-col gap-2">
            <p
              data-testid="login-forgot-success"
              className="text-ink text-sm"
            >
              If an account exists for that username, reset
              instructions are on the way. The reset link goes to
              the email on file.
            </p>
            {devResetToken ? (
              <div
                data-testid="login-forgot-dev-token"
                className="flex flex-col gap-1 rounded border border-rule bg-elevated p-2 text-[0.65rem] font-mono"
              >
                <span className="text-ink-muted">
                  Dev reset URL:
                </span>
                <code className="text-ink break-all">
                  {buildResetUrl(devResetToken)}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard?.writeText(
                      buildResetUrl(devResetToken),
                    );
                  }}
                  data-testid="login-forgot-dev-copy"
                  className="self-start rounded border border-rule px-2 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-ink-muted hover:bg-field hover:text-ink"
                >
                  Copy link
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-display uppercase tracking-wider text-ink-muted">
                Username
              </span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoFocus
                data-testid="login-forgot-username"
                className="rounded border border-rule bg-surface px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </label>
            <p className="text-[0.6rem] font-mono text-ink-muted">
              We'll send reset instructions to the email on file
              for that username.
            </p>
            {error ? (
              <p
                data-testid="login-forgot-error"
                className="text-destructive text-xs font-mono"
              >
                {error}
              </p>
            ) : null}
          </div>
        )
      }
      confirmLabel={
        success
          ? "Close"
          : submitting
            ? "Sending…"
            : "Send reset instructions"
      }
      onConfirm={() => {
        if (success) onClose();
        else void submit();
      }}
      onClose={onClose}
      data-testid="login-forgot-modal"
    />
  );
}

function buildResetUrl(token: string): string {
  if (typeof window === "undefined") return `/reset-password?token=${token}`;
  return `${window.location.origin}/reset-password?token=${encodeURIComponent(token)}`;
}

function formatLoginErr(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return "Wrong username or password.";
    if (err.status === 429) {
      return "Too many sign-in attempts. Try again in a minute.";
    }
    return typeof err.detail === "string"
      ? err.detail
      : `Sign-in failed (HTTP ${err.status}).`;
  }
  return "Network error. Try again.";
}

function authErrorTitle(code: string | undefined): string {
  switch ((code || "").trim()) {
    case "account_link_required":
      return "Account needs review";
    case "disabled_user":
      return "Account disabled";
    case "verified_email_required":
      return "Verify your email";
    default:
      return "Account access needs attention";
  }
}

function authErrorPageTitle(code: string | undefined): string {
  switch ((code || "").trim()) {
    case "account_link_required":
      return "Account needs review";
    case "disabled_user":
      return "Account disabled";
    case "verified_email_required":
      return "Please verify your email";
    default:
      return "Account access needs attention";
  }
}

function authErrorPageDescription(code: string | undefined): string {
  switch ((code || "").trim()) {
    case "account_link_required":
      return "Tktic found an existing local account record for that email, so access is paused until the account is reviewed.";
    case "disabled_user":
      return "This account exists, but it is disabled. A club admin needs to restore access before sign-in can continue.";
    case "verified_email_required":
      return "Check your inbox and verify your email before continuing to your workspace.";
    default:
      return "Secure account access could not finish. Review the message below, then try again.";
  }
}

function authErrorCopy(code: string | undefined): string | null {
  switch ((code || "").trim()) {
    case "account_link_required":
      return "A Tktic account record already uses that email. Ask an admin to review existing accounts for this address or remove the stale account record, then try again.";
    case "email_mismatch":
      return "The sign-in email did not match the verified email on the Tktic account you were linking.";
    case "identity_already_linked":
      return "That sign-in method is already linked to another Tktic account.";
    case "verified_email_required":
      return "We sent a verification email to the address you used. Open it, verify your email, then come back and continue.";
    case "disabled_user":
      return "This Tktic account is disabled. Contact your team admin for access.";
    case "email_required":
    case "missing_subject":
      return "The sign-in provider did not return the account details Tktic needs to finish sign-in.";
    case "auth_failed":
      return "Secure sign-in could not finish. Please try again.";
    default:
      return null;
  }
}
